import { ReactNode } from 'react'

import { CommonArb, CommonBase, CommonBitcoin, CommonBnb, CommonEthereum, CommonOp, CommonSolana } from '@/icons'
import { SUPPORTED_NETWORK } from '@/utils'

import { type Chain } from 'viem'
import {
  arbitrum,
  arbitrumSepolia,
  base,
  baseSepolia,
  bsc,
  mainnet,
  optimism,
  optimismSepolia,
  sepolia,
} from 'viem/chains'

// please dont change value of this enum
export enum EChainType {
  BTC = 'BTC',
  EVM = 'EVM',
  SOLANA = 'SOLANA',
  OPTIMEX = 'OPTIMEX',
}

type ChainConfig = {
  getIcon: ((props: React.ComponentProps<'svg'>) => ReactNode) | null
  minForGas: bigint
  chainId: number // onchain chain id
  getExplorerUrl: (type: string, txHash: string) => string
  type: EChainType
  wagmiChain: Chain | null
}

const BTC: ChainConfig = {
  getIcon: (props: React.ComponentProps<'svg'>) => <CommonBitcoin {...props} />,
  minForGas: 5000n, // 0.00005
  chainId: 0,
  getExplorerUrl: (type: string, txHash: string) => `https://mempool.space/${type}/${txHash}`,
  type: EChainType.BTC,
  wagmiChain: null,
}

const BTC_TESTNET: ChainConfig = {
  getIcon: (props: React.ComponentProps<'svg'>) => <CommonBitcoin {...props} />,
  minForGas: 5000n, // 0.00005
  chainId: 0,
  getExplorerUrl: (type: string, txHash: string) => `https://mempool.space/testnet/${type}/${txHash}`,
  type: EChainType.BTC,
  wagmiChain: null,
}

const SOLANA: ChainConfig = {
  getIcon: (props: React.ComponentProps<'svg'>) => <CommonSolana {...props} />,
  minForGas: 10000000n, // 0.01
  chainId: 0,
  getExplorerUrl: (type: string, txHash: string) => `https://solscan.io/${type}/${txHash}`,
  type: EChainType.SOLANA,
  wagmiChain: null,
}

const SOLANA_DEVNET: ChainConfig = {
  getIcon: (props: React.ComponentProps<'svg'>) => <CommonSolana {...props} />,
  minForGas: 10000000n, // 0.01
  chainId: 0,
  getExplorerUrl: (type: string, txHash: string) => `https://solscan.io/${type}/${txHash}?cluster=devnet`,
  type: EChainType.SOLANA,
  wagmiChain: null,
}

const ETHEREUM: ChainConfig = {
  getIcon: (props: React.ComponentProps<'svg'>) => <CommonEthereum {...props} />,
  minForGas: 10000000000000000n, // 0.01
  chainId: 1,
  getExplorerUrl: (type: string, txHash: string) => `https://etherscan.io/${type}/${txHash}`,
  type: EChainType.EVM,
  wagmiChain: mainnet,
}

const ETHEREUM_SEPOLIA: ChainConfig = {
  getIcon: (props: React.ComponentProps<'svg'>) => <CommonEthereum {...props} />,
  minForGas: 10000000000000000n, // 0.01
  chainId: 11155111,
  getExplorerUrl: (type: string, txHash: string) => `https://sepolia.etherscan.io/${type}/${txHash}`,
  type: EChainType.EVM,
  wagmiChain: sepolia,
}

const BASE_SEPOLIA: ChainConfig = {
  getIcon: (props: React.ComponentProps<'svg'>) => <CommonBase {...props} />,
  minForGas: 1000000000000000n, // 0.001
  chainId: 84532,
  getExplorerUrl: (type: string, txHash: string) => `https://sepolia.basescan.org/${type}/${txHash}`,
  type: EChainType.EVM,
  wagmiChain: baseSepolia,
}

const BASE: ChainConfig = {
  getIcon: (props: React.ComponentProps<'svg'>) => <CommonBase {...props} />,
  minForGas: 1000000000000000n, // 0.001
  chainId: 8453,
  getExplorerUrl: (type: string, txHash: string) => `https://basescan.org/${type}/${txHash}`,
  type: EChainType.EVM,
  wagmiChain: base,
}

const ARBITRUM_SEPOLIA: ChainConfig = {
  getIcon: (props: React.ComponentProps<'svg'>) => <CommonArb {...props} />,
  minForGas: 1000000000000000n, // 0.001
  chainId: 421614,
  getExplorerUrl: (type: string, txHash: string) => `https://sepolia.arbiscan.io/${type}/${txHash}`,
  type: EChainType.EVM,
  wagmiChain: arbitrumSepolia,
}

const ARBITRUM: ChainConfig = {
  getIcon: (props: React.ComponentProps<'svg'>) => <CommonArb {...props} />,
  minForGas: 1000000000000000n, // 0.001
  chainId: 42161,
  getExplorerUrl: (type: string, txHash: string) => `https://arbiscan.io/${type}/${txHash}`,
  type: EChainType.EVM,
  wagmiChain: arbitrum,
}

const OPTIMISM: ChainConfig = {
  getIcon: (props: React.ComponentProps<'svg'>) => <CommonOp {...props} />,
  minForGas: 1000000000000000n, // 0.001
  chainId: 10,
  getExplorerUrl: (type: string, txHash: string) => `https://optimistic.etherscan.io/${type}/${txHash}`,
  type: EChainType.EVM,
  wagmiChain: optimism,
}

const OPTIMISM_SEPOLIA: ChainConfig = {
  getIcon: (props: React.ComponentProps<'svg'>) => <CommonOp {...props} />,
  minForGas: 1000000000000000n, // 0.001
  chainId: 11155420,
  getExplorerUrl: (type: string, txHash: string) => `https://sepolia-optimism.etherscan.io/${type}/${txHash}`,
  type: EChainType.EVM,
  wagmiChain: optimismSepolia,
}

const BSC: ChainConfig = {
  getIcon: (props: React.ComponentProps<'svg'>) => <CommonBnb {...props} />,
  minForGas: 1000000000000000n, // 0.001
  chainId: 56,
  getExplorerUrl: (type: string, txHash: string) => `https://bscscan.com/${type}/${txHash}`,
  type: EChainType.EVM,
  wagmiChain: bsc,
}

const OPTIMEX: ChainConfig = {
  getIcon: null,
  minForGas: 5000n, // 0.00005
  chainId: 0,
  getExplorerUrl: (type: string, txHash: string) => `https://explorer-bitfi-p00c4t1rul.t.conduit.xyz/${type}/${txHash}`,
  type: EChainType.OPTIMEX,
  wagmiChain: null,
}

export const CHAIN_CONFIGS: Record<string, ChainConfig> = {
  [SUPPORTED_NETWORK.OPTIMEX]: OPTIMEX,
  [SUPPORTED_NETWORK.BTC]: BTC,
  [SUPPORTED_NETWORK.ETHEREUM]: ETHEREUM,
  [SUPPORTED_NETWORK.SOLANA]: SOLANA,
  [SUPPORTED_NETWORK.BTC_TESTNET]: BTC_TESTNET,
  [SUPPORTED_NETWORK.ETHEREUM_SEPOLIA]: ETHEREUM_SEPOLIA,
  [SUPPORTED_NETWORK.SOLANA_DEVNET]: SOLANA_DEVNET,
  [SUPPORTED_NETWORK.BASE_SEPOLIA]: BASE_SEPOLIA,
  [SUPPORTED_NETWORK.BASE]: BASE,
  [SUPPORTED_NETWORK.ARBITRUM]: ARBITRUM,
  [SUPPORTED_NETWORK.ARBITRUM_SEPOLIA]: ARBITRUM_SEPOLIA,
  [SUPPORTED_NETWORK.OPTIMISM]: OPTIMISM,
  [SUPPORTED_NETWORK.OPTIMISM_SEPOLIA]: OPTIMISM_SEPOLIA,
  [SUPPORTED_NETWORK.BSC]: BSC,
}

export const CHAIN_ID_TO_NETWORK_ID: Record<number, SUPPORTED_NETWORK> = {
  [BTC.chainId]: SUPPORTED_NETWORK.BTC,
  [BTC_TESTNET.chainId]: SUPPORTED_NETWORK.BTC_TESTNET,
  [SOLANA.chainId]: SUPPORTED_NETWORK.SOLANA,
  [SOLANA_DEVNET.chainId]: SUPPORTED_NETWORK.SOLANA_DEVNET,
  [ETHEREUM.chainId]: SUPPORTED_NETWORK.ETHEREUM,
  [ETHEREUM_SEPOLIA.chainId]: SUPPORTED_NETWORK.ETHEREUM_SEPOLIA,
  [BASE_SEPOLIA.chainId]: SUPPORTED_NETWORK.BASE_SEPOLIA,
  [BASE.chainId]: SUPPORTED_NETWORK.BASE,
  [ARBITRUM_SEPOLIA.chainId]: SUPPORTED_NETWORK.ARBITRUM_SEPOLIA,
  [ARBITRUM.chainId]: SUPPORTED_NETWORK.ARBITRUM,
  [OPTIMISM.chainId]: SUPPORTED_NETWORK.OPTIMISM,
  [OPTIMISM_SEPOLIA.chainId]: SUPPORTED_NETWORK.OPTIMISM_SEPOLIA,
  [BSC.chainId]: SUPPORTED_NETWORK.BSC,
}
